// Default Footer Component
import React from 'react';
import { useIntl } from 'react-intl';
import Logo from '../../../customizations/components/theme/Logo/Logo';
import config from '@plone/volto/registry';
import { PageSection, ContentWrapper } from '../../Layout';
import { useSelector } from 'react-redux';
import { ESSButton } from '../../Atoms/ESSButton';
import BscLogo from '../../../images/BSC-white-large.png';
import './FooterDefault.scss';

interface SocialMedia {
  name: string;
  url: string;
  icon: string;
}

interface FooterConfig {
  secondaryLogo?: string;
  contactText?: string;
  contactButtonText?: string;
  contactUrl?: string;
  handlePrivacyClick?: () => void;
  handleLegalClick?: () => void;
  socialMedia?: SocialMedia[];
}

// Level 1: Default footer configuration (addon fallback)
const defaultFooterConfig: FooterConfig = {
  secondaryLogo: BscLogo, // Optional: set to image import or null to hide
  contactText: 'For all enquiries, please email us using the form here.',
  contactButtonText: 'CONTACT US',
  contactUrl: '/contact-info',

  handleLegalClick: () => {
    if (typeof window !== 'undefined') {
       window.open('https://www.bsc.es/legal-notice');
    }
  },
  socialMedia: [
    { name: 'Twitter', url: 'https://twitter.com/BSC_CNS', icon: 'twitter' },
    { name: 'Facebook', url: 'https://facebook.com/bsc', icon: 'facebook' },
    { name: 'YouTube', url: 'https://www.youtube.com/channel/UC-jQw5yIjnUTRAg4DwOjbAQ', icon: 'youtube' },
  ],
};

interface SocialIconProps {
  icon: string;
}

const FooterDefault: React.FC = () => {
  const intl = useIntl();
  const { settings } = config;
  const year = new Date().getFullYear();
  
  // Level 3: Backend configuration (highest priority)
  const backendFooterData = useSelector(
    (state: any) => state.content?.data?.footerData,
  );
  
  // Level 2: Project configuration from config.settings
  const projectFooterConfig: Partial<FooterConfig> = settings.footerConfig || {};
  
  // Merge configurations: Backend > Project > Defaults
  const footerData: FooterConfig = {
    ...defaultFooterConfig,
    ...(projectFooterConfig || {}),
    ...(backendFooterData || {}),
  };

  // Social media icon components
  const SocialIcon: React.FC<SocialIconProps> = ({ icon }) => {
    const iconMap: Record<string, JSX.Element> = {
      twitter: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      facebook: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      youtube: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
      linkedin: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      instagram: (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
        </svg>
      ),
    };
    return iconMap[icon] || null;
  };

  const footerSectionStyles = {
    backgroundColor: 'var(--footer-statedefault-bgcolor)',
    borderTop: 'var(--footer-statedefault-border)',
  };

  const footerWrapperStyles = {
    color: 'var(--footer-statedefault-fgcolor)'
  };
  
  const subfooterSectionStyles = {
    backgroundColor: 'var(--subfooter-statedefault-bgcolor)',
      borderTop: 'var(--subfooter-statedefault-border)',
  };

  const subfooterWrapperStyles = {
    color: 'var(--subfooter-statedefault-fgcolor)',
  };

  return (
    <footer className="ess-footer">
     {/* Main Footer */}
      <PageSection padding={false} style={footerSectionStyles}>
        <ContentWrapper className="footer-main" style={footerWrapperStyles}>
          <div className="footer-content">
            {/* Left: Primary Logo + Optional Secondary Logo */}
            <div className="footer-branding">
              <div className="logo-wrapper">
                <Logo desktopHeight="55px" mobileHeight="45px" color={'inherit'} />
              </div>
              {footerData.secondaryLogo && (
                <div className="logo-wrapper logo-wrapper--secondary">
                  <img 
                    src={footerData.secondaryLogo} 
                    alt="Secondary Logo"
                    className="secondary-logo"
                  />
                </div>
              )}
            </div>

            {/* Right: Contact Text + Button */}
            <div className="footer-right">
              <div className="footer-contact">
                <p className="contact-text">
                  {footerData.contactText}
                </p>
              </div>

              <div className="footer-cta">
                <ESSButton 
                  variant="negative" 
                  size="md"
                  href="/contact-info"
                >
                  {footerData.contactButtonText}
                </ESSButton>
              </div>
            </div>
          </div>
        </ContentWrapper>
      </PageSection>
      
      {/* Sub Footer */}
      <PageSection padding={false} style={subfooterSectionStyles}>
        <ContentWrapper className="footer-sub" style={subfooterWrapperStyles}>
          <div className="subfooter-content">
            {/* Left: Copyright + Links */}
            <div className="subfooter-left">
              <span className="copyright">
                © {year} All Rights Reserved
              </span>
              <span className="separator">|</span>
              <ESSButton 
                variant="link-icon"
                href="/privacy-policy"
                aria-label="Privacy Policy"
                className="footer-link">
                Privacy Policy
              </ESSButton>
              <span className="separator">|</span>
              <ESSButton 
                variant="link-icon"
                onClick={footerData.handleLegalClick}
                aria-label="Legal Notice"
                className="footer-link">
                Legal Notice
              </ESSButton>
            </div>
            {/* Right: Social Media */}
            <div className="subfooter-right">
              <div className="social-media">
                {footerData.socialMedia?.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    className="social-icon"
                  >
                    <SocialIcon icon={social.icon} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </ContentWrapper>
      </PageSection>
    </footer>
  );
};

export default FooterDefault;
